/* JsonDiffNavigationComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const JsonDiffNavigationRenderer = require("./JsonDiffNavigationRenderer");
const _ = require("lodash");

class JsonDiffNavigationComponent extends EventEmitter {
    constructor(config) {
        super(config);
        this._leftEditor = config.leftEditor;
        this._rightEditor = config.rightEditor;
        this._markers = _.get(config, "markers", []);
        this._scrollSynchronized = _.get(config, "scrollSynchronized", true);
        this._compactView = _.get(config, "compactView", false);
        this._sideBySideView = _.get(config, "sideBySideView", true);
        this._theme = _.get(config, "theme", "dark");

        this._renderer = new JsonDiffNavigationRenderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}

    _triggerScrollSynchronizedValueChanged() {
        this.emit("scrollSynchronizedValueChanged", this._scrollSynchronized);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        if (!this.renderer) {
            return Promise.resolve();
        } else {
            return this._renderer.shutdown().then(
                () => {
                    this._leftEditor = undefined;
                    this._rightEditor = undefined;
                    this._renderer = undefined;
                },
                () => {
                    //Error handling on renderer shut down
                }
            );
        }
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setMarkers.name setMarkers
     * @schema.methods.setMarkers.description Set difference highlight markers used in navigating them
     * @schema.methods.setMarkers.returns Instance of the control
     * @schema.methods.setMarkers.parameters.markers.name markers
     * @schema.methods.setMarkers.parameters.markers.description new markers
     * @schema.methods.setMarkers.parameters.markers.type Array
     */
    setMarkers(markers) {
        if (!_.isEqual(markers, this._markers)) {
            this._markers = [...markers];
            this.emit("markers", this._markers);
        }
        return this;
    }

    /**
     * @schema.methods.getMarkers.name getMarkers
     * @schema.methods.getMarkers.description Returns difference highlight markers
     * @schema.methods.getMarkers.returns Object
     */
    getMarkers() {
        return this._markers;
    }

    set markers(markers) {
        this.setMarkers(markers);
    }

    get markers() {
        return this.getMarkers();
    }

    /**
     * @schema.methods.setLeftEditor.name setLeftEditor
     * @schema.methods.setLeftEditor.description Set left ace editor instance
     * @schema.methods.setLeftEditor.returns Instance of the control
     * @schema.methods.setLeftEditor.parameters.leftEditor.name leftEditor
     * @schema.methods.setLeftEditor.parameters.leftEditor.description left ace editor instance
     * @schema.methods.setLeftEditor.parameters.leftEditor.type Object
     */
    setLeftEditor(leftEditor) {
        if (this._leftEditor !== leftEditor) {
            this._leftEditor = leftEditor;
        }
        return this;
    }

    /**
     * @schema.methods.getLeftEditor.name getLeftEditor
     * @schema.methods.getLeftEditor.description Returns left editor instance
     * @schema.methods.getLeftEditor.returns Object
     */
    getLeftEditor() {
        return this._leftEditor;
    }

    set leftEditor(leftEditor) {
        this.setLeftEditor(leftEditor);
    }

    get leftEditor() {
        return this.getLeftEditor();
    }

    /**
     * @schema.methods.setRightEditor.name setRightEditor
     * @schema.methods.setRightEditor.description Set right ace editor instance
     * @schema.methods.setRightEditor.returns Instance of the control
     * @schema.methods.setRightEditor.parameters.rightEditor.name rightEditor
     * @schema.methods.setRightEditor.parameters.rightEditor.description right ace editor instance
     * @schema.methods.setRightEditor.parameters.rightEditor.type Object
     */
    setRightEditor(rightEditor) {
        if (this._rightEditor !== rightEditor) {
            this._rightEditor = rightEditor;
        }
        return this;
    }

    /**
     * @schema.methods.getRightEditor.name getRightEditor
     * @schema.methods.getRightEditor.description Returns right editor instance
     * @schema.methods.getRightEditor.returns Object
     */
    getRightEditor() {
        return this._rightEditor;
    }

    set rightEditor(rightEditor) {
        this.setRightEditor(rightEditor);
    }

    get rightEditor() {
        return this.getRightEditor();
    }

    /**
     * @schema.methods.setScrollSynchronized.name setScrollSynchronized
     * @schema.methods.setScrollSynchronized.description Scroll on editor auto scrolls other editor to show differences in parallel
     *                                                          or as close to each other as possible
     * @schema.methods.setScrollSynchronized.returns Instance of the control
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.name scrollSynchronized
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.description true enables synchronized scroll
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.type Boolean
     */
    setScrollSynchronized(scrollSynchronized) {
        if (scrollSynchronized !== this._scrollSynchronized && _.isBoolean(scrollSynchronized)) {
            this._scrollSynchronized = scrollSynchronized;
            this.emit("scrollSynchronized", scrollSynchronized);
        }
        return this;
    }

    /**
     * @schema.methods.isScrollSynchronized.name isScrollSynchronized
     * @schema.methods.isScrollSynchronized.description true if synchronized scroll enabled else false
     * @schema.methods.isScrollSynchronized.returns Boolean
     */
    isScrollSynchronized() {
        return this._scrollSynchronized;
    }

    set scrollSynchronized(scrollSynchronized) {
        this.setScrollSynchronized(scrollSynchronized);
    }

    get scrollSynchronized() {
        return this.isScrollSynchronized();
    }

    /**
     * @schema.methods.setCompactView.name setCompactView
     * @schema.methods.setCompactView.description  Collapses unchanged rows if true
     * @schema.methods.setCompactView.returns Instance of the control
     * @schema.methods.setCompactView.parameters.compactView.name compactView
     * @schema.methods.setCompactView.parameters.compactView.description true enables compact view
     * @schema.methods.setCompactView.parameters.compactView.type Boolean
     */
    setCompactView(compactView) {
        if (compactView !== this._compactView && _.isBoolean(compactView)) {
            this._compactView = compactView;
            this.emit("compactView", compactView);
        }
        return this;
    }

    /**
     * @schema.methods.isCompactView.name isCompactView
     * @schema.methods.isCompactView.description true if compact view enabled else false
     * @schema.methods.isCompactView.returns Boolean
     */
    isCompactView() {
        return this._compactView;
    }

    set compactView(compactView) {
        this.setCompactView(compactView);
    }

    get compactView() {
        return this.isCompactView();
    }

    /**
     * @schema.methods.setSideBySideView.name setSideBySideView
     * @schema.methods.setSideBySideView.description  If true Shows differences side by side on UI
     *                                                else shows differences in single editor one after the other
     * @schema.methods.setSideBySideView.returns Instance of the control
     * @schema.methods.setSideBySideView.parameters.sideBySideView.name sideBySideView
     * @schema.methods.setSideBySideView.parameters.sideBySideView.description true enables side by side view
     * @schema.methods.setSideBySideView.parameters.sideBySideView.type Boolean
     */
    setSideBySideView(sideBySideView) {
        if (sideBySideView !== this._sideBySideView && _.isBoolean(sideBySideView)) {
            this._sideBySideView = sideBySideView;
            this.emit("sideBySideView", sideBySideView);
        }
        return this;
    }

    /**
     * @schema.methods.isSideBySideView.name isSideBySideView
     * @schema.methods.isSideBySideView.description true if side by side view enabled else false
     * @schema.methods.isSideBySideView.returns Boolean
     */
    isSideBySideView() {
        return this._sideBySideView;
    }

    set sideBySideView(sideBySideView) {
        this.setSideBySideView(sideBySideView);
    }

    get sideBySideView() {
        return this.isSideBySideView();
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = JsonDiffNavigationComponent;
